<template>
  <div class="wt2-slider" @click="focusOnInput">
    <div
      class="-seek-label-box"
      @mouseenter="inSlidersSeek = true"
      @mouseleave="
        inSlidersSeek = false;
        seekOffsetX = null;
      "
      @mousemove="inSlidersSeek && recordOffsetX($event)"
    >
      <div class="-slider-header">
        <div class="-label-box">
          <slot name="icon"></slot>
          <slot name="header"></slot>
          <div class="-input-box" v-if="showInputBox && $slots.header">
            <numeric-text
              ref="input"
              type="text"
              v-model="curValue"
              :max="max"
              :min="min"
              :step="sliderStep"
              :disabled="disabled"
              :format="format"
            />
            <slot name="input-box-holder"></slot>
          </div>
        </div>
      </div>
      <div v-if="showSlider" class="-seek-box" data-prevent-click="true">
        <numeric-range
          v-model="curValue"
          :max="max"
          :min="min"
          :disabled="disabled"
          :format="format"
          :step="sliderStep"
          :points-snap="snapMarkers"
          @change="rangeChange"
          @focus="rangeFocus"
        />

        <div
          class="-slider-markers"
          :class="{ 'no-marks': mapMarkers.length <= 2 }"
          ref="markers"
          v-if="showStepsMarker"
        >
          <numeric-marker
            v-for="marker in mapMarkers"
            :showLine="mapMarkers.length > 2"
            :key="marker.val"
            :style="getMarkerStyle(marker.val)"
            :value="marker.val"
            :step="sliderStep"
            :active="marker.isActive"
          />
        </div>
      </div>
    </div>
    <div class="-input-box" v-if="showInputBox && !$slots.header">
      <numeric-text
        type="text"
        v-model="curValue"
        :step="sliderStep"
        :max="max"
        :min="min"
        :disabled="disabled"
        :format="format"
      />
    </div>
  </div>
</template>

<script>
import numericRange from './parts/numericRange.vue';
import numericText from './parts/numericText.vue';
import numericMarker from './parts/numericMarker.vue';

export default {
  model: {
    prop: 'value',
    event: 'change',
  },
  components: {
    numericRange,
    numericText,
    numericMarker,
  },

  props: {
    value: {
      type: [String, Number],
      default: 0,
    },

    max: {
      type: Number,
      default: 100,
    },

    min: {
      type: Number,
      default: 0,
    },

    steps: {
      type: Array,
      default: () => [],
    },

    showStepsMarker: {
      type: Boolean,
      default: true,
    },

    showStepsMarkerValue: {
      type: Boolean,
      default: false,
    },

    showInputBox: {
      type: Boolean,
      default: true,
    },

    showSlider: {
      type: Boolean,
      default: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    format: {
      type: Function,
      default: (value) => parseFloat(value),
    },

    offsetX: {
      type: Number,
      default: 25,
    },
    sliderStep: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      curValue: null,
      inSlidersSeek: false,
      seekOffsetX: null,
    };
  },

  computed: {
    markers() {
      return [this.min, ...this.steps, this.max];
    },

    mapMarkers() {
      return _.map(this.markers, (val, index) => {
        return {
          val: _.isFunction(this.format) ? this.format(val) : val,
          isActive: this.isMarkerActive(
            index,
            this.seekOffsetX,
            index === 0 || index === this.markers.length - 1 // is first or last
          ),
        };
      });
    },

    snapMarkers() {
      return _.isEmpty(this.steps) ? [] : this.markers;
    },
  },

  watch: {
    value(newValue) {
      this.curValue = newValue;
    },
  },

  created() {
    this.curValue = _.isDefined(this.value) ? this.value : 0;
    this.$watch('curValue', (newValue) => {
      this.$emit('change', newValue);
    });
  },

  methods: {
    getMarkerPosition(value) {
      return ((value - this.min) / (this.max - this.min)) * 100;
    },

    getMarkerStyle(value) {
      const markerPosition = this.getMarkerPosition(value);
      const markers = this.markers;
      const middle = markers.length / 2;
      const middleValue = this.format(markers[Math.floor(middle)]);

      const style = {
        left: markerPosition + '%',
      };

      if (markerPosition > 0 && markerPosition < 100 && value !== middleValue) {
        style.transform = `translateX(${value > middleValue ? -4 : 4}px)`;
      }

      return style;
    },

    recordOffsetX(e) {
      this.seekOffsetX = (e && e.offsetX) || null;
    },

    isMarkerActive(index, offsetX, isFirstOrLast) {
      if (this.showStepsMarkerValue || isFirstOrLast) {
        return true;
      }

      if (!this.inSlidersSeek || !offsetX) {
        return false;
      }

      const markers = this.$refs.markers;

      if (!markers) {
        return false;
      }

      const curMarker = markers.children[index];
      const nextMarker = markers.children[index + 1];
      const prevMarker = markers.children[index - 1];

      const curOffset = curMarker && curMarker.offsetLeft;
      const nextOffset = nextMarker && nextMarker.offsetLeft;
      const prevOffset = prevMarker && prevMarker.offsetLeft;

      // is offsetX between nextOffset or prevOffset
      // and is the marker close enough to be active (this.offsetX)
      return (
        ((offsetX > curOffset && offsetX < nextOffset) ||
          (offsetX < curOffset && offsetX > prevOffset)) &&
        (offsetX >= curOffset
          ? offsetX - this.offsetX <= curOffset
          : offsetX + this.offsetX >= curOffset)
      );
    },
    focusOnInput($event) {
      let el = $event.target;
      while (el !== this.$el) {
        if (el.dataset.preventClick) {
          return;
        }
        el = el.parentElement;
      }
      if (this.$refs.input) {
        this.$refs.input.$el.querySelector('input').select();
      }
    },
    rangeChange(value) {
      this.$emit('slider-drag-end', value);
    },
    rangeFocus() {
      this.$emit('slider-drag-start');
    },
  },
};
</script>
